import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, NgZone, SimpleChanges, ViewChild } from '@angular/core';
import { SocketService } from '../../../../utility/services/common/socket.service';
import { LstorageService } from '../../../services/common/lstorage.service';
import { BookingService } from '../../../services/booking/booking.service';
import { BookingSyncService } from '../../../services/booking-sync/booking-sync.service';
import { DatePipe } from '@angular/common';
import { SnackbarService } from '../../../services/common/snackbar.service';


@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatComponent {
  @Input() chatDataReceived: any | {};
  @Input() chatVisible = false;
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  receiveMsg: any = {};
  sendMsg: String = '';
  messages: any[] = [];
  receiveMsgData: any = [];

  // NEW: track whether the user is actually looking at this chat right now
  private isWindowFocused = true;
  private isDocVisible = true;

  private get isChatActive(): boolean {
    return this.chatVisible && this.isWindowFocused && this.isDocVisible;
  }
  constructor(
    private socketServ: SocketService,
    private lstorageServ: LstorageService,
    private bookingServ: BookingService,
    private bookingSyncServ: BookingSyncService,
    private datePipe: DatePipe,
    private snackbarServ: SnackbarService,
    private cdr: ChangeDetectorRef,
  ) { }


  @HostListener('window:focus')
  onWindowFocus() {
    this.isWindowFocused = true;
    this.markAsReadIfActive();
  }

  @HostListener('window:blur')
  onWindowBlur() {
    this.isWindowFocused = false;
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    this.isDocVisible = document.visibilityState === 'visible';
    if (this.isDocVisible) {
      this.markAsReadIfActive();
    }
  }

  ngOnInit() {
    this.isWindowFocused = document.hasFocus();
    this.isDocVisible = document.visibilityState === 'visible';
    this.bookingSyncServ.getUserDataForChat().subscribe(res => {
      this.receiveMsgData = res;
      if (res?.orderId && res?.driverId) {
        this.fetchOldChats(() => {
        if (this.chatVisible) {
          this.markAsReadIfActive();
        }
      });
      }
      this.cdr.detectChanges();
    });

    this.socketServ.listen('messageRead').subscribe((data: any) => {

      this.messages = this.messages.map(msg => {

        if (
          msg.order_id == data.order_id &&
          msg.send_by == data.send_by && 
          msg.send_to == data.seen_by_user_id
        ) {
          return {
            ...msg,
            unread: '0'
          };
        }

        return msg;
      });

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });


    this.socketServ.listen('receiveMessage').subscribe((data: any) => {
      if ((this.chatDataReceived.order_id != data.order_id) && this.chatVisible) {
        return;
      }
      this.receiveMsgData = { ...this.receiveMsgData, ...data };

      let formattedTime = this.convertUtcToLocalTime(data.sent_at);

      let newData = { ...data, sent_at: formattedTime };

      if (this.isChatActive) {
        newData.unread = '0';
      }
      this.messages = [...this.messages, newData];
      this.scrollToBottom();

      if (this.isChatActive) {
        this.emitReadEvent();
      }

      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });

     this.markAsReadIfActive();

  }
   ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['chatVisible'] &&
      changes['chatVisible'].currentValue === true
    ) {

      setTimeout(() => {
        this.markAsReadIfActive();
      });
    }
  }
  
  formatTime(date: string): string {
    return new Date(date).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
  shouldShowDateDivider(index: number): boolean {
    if (index === 0) return true;

    const current = new Date(this.messages[index].sent_at);
    const previous = new Date(this.messages[index - 1].sent_at);

    return current.toDateString() !== previous.toDateString();
  }
  getDateLabel(date: string): string {
    const msgDate = new Date(date);

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) {
      return 'Today';
    }

    if (msgDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    return msgDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  private markAsReadIfActive() {
    if (!this.isChatActive) return;

    const hasUnreadFromDriver = this.messages.some(
      m => m.send_by == this.receiveMsgData?.driverId && m.send_to == this.receiveMsgData?.
        userId && m.unread === '1'
    );

    if (hasUnreadFromDriver) {
      this.emitReadEvent();
      this.messages = this.messages.map(m =>
        m.send_by == this.receiveMsgData?.driverId && m.send_to == this.receiveMsgData?.
          userId ? { ...m, unread: '0' } : m
      );
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  onEnter(event: any): void {
    event.preventDefault();
    this.sendMessage();
  }
  sendMessage() {
    if (this.sendMsg && this.sendMsg.length > 200) {
      this.snackbarServ.showError("Message cannot exceed 200 characters.");
      return;
    }

    if (this.sendMsg.trim()) {
      const now = new Date();

      let msg = {
        to: this.receiveMsgData?.driverId,
        to_user_id: this.receiveMsgData?.driverId,
        to_user_detail_id: this.receiveMsgData?.driver_user_detail_id,
        user_detail_id: this.receiveMsgData?.userId,
        text: this.sendMsg?.trim(),
        order_id: this.receiveMsgData?.orderId,
        sent_at: this.datePipe.transform(now, 'yyyy-MM-dd HH:mm:ss', 'UTC') || '',
        original: '',
        thumbnail: '',
        chat_type: 'text'
      };

      const msgDetails = {
        detail: {...msg,user_type_id: this.receiveMsgData?.user_type_id},
      }
      this.socketServ.emitSendMessage('sendMessage', msgDetails, (response: any) => {
      if (response?.success || response?.statusCode === 200) {
        const formattedMsg = {
          unread: "1",
          send_to_deleted: "0",
          send_by_deleted: "0",
          c_id: null,
          send_to: msg.to_user_id,
          send_by: msg.user_detail_id,
          text: msg.text,
          order_id: String(msg.order_id),
          sent_at: this.convertUtcToLocalTime(msgDetails.detail.sent_at),
          original: msg.original,
          thumbnail: msg.thumbnail,
          chat_type: msg.chat_type,
        };
        this.messages = [...this.messages, formattedMsg];

        this.cdr.detectChanges();
        this.scrollToBottom();
      } else {
        this.snackbarServ.showError(response?.msg || 'Failed to send message. Please try again.');
      }
    });

      this.sendMsg = '';
      this.scrollToBottom();

    } else {
      console.warn('Cannot send an empty message.');
    }
  }

  private emitReadEvent() {
    if (!this.receiveMsgData?.orderId || !this.receiveMsgData?.driverId || !this.receiveMsgData?.driver_user_detail_id) {
      return;
    }

    this.socketServ.emitSendMessage(
      'read',
      {
        detail: {
          order_id: this.receiveMsgData.orderId.toString(),
          send_by: this.receiveMsgData.driverId.toString(),
          sender_user_detail_id:this.receiveMsgData.driver_user_detail_id.toString(),
          seen_by_user_id: this.receiveMsgData.userId.toString()
        }
      }
    );
  }

  fetchOldChats(callback?: () => void) {
    let payload = {
      order_id: this.receiveMsgData?.orderId,
      receiver_id: this.receiveMsgData.driverId,
      limit: 100,
      skip: 0
    }
    this.bookingServ.fetchOldChat(payload).subscribe((res: any) => {
      if (res.success) {
        this.messages = res.result.map((message: any) => ({
          ...message,
          sent_at: this.convertUtcToLocalTime(message.sent_at)
        }));
        this.scrollToBottom();
        this.cdr.markForCheck();
        if (callback) {
          callback();
        }
      } else {
        this.snackbarServ.showError(res.msg);
      }
    }
    );
  }
  scrollToBottom(): void {
    setTimeout(() => {
      try {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      } catch (err) {
        // console.error('Scroll Error:', err);
      }
    }, 100);
  }

  convertUtcToLocalTime(utcTime: string): string {
    if (!utcTime) return "";

    let dateStr = utcTime;
    if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.toUpperCase().includes('UTC')) {
      if (dateStr.includes('T')) {
        dateStr += 'Z';
      } else {
        dateStr += ' UTC';
      }
    }

    let dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) {
      return utcTime;
    }

    return this.datePipe.transform(dateObj, 'yyyy-MM-dd HH:mm:ss') || '';
  }


}
